// Public /vitrine: the publications that run on delapresse, with a
// production screenshot each. Static data (src/lib/showcase-sites.ts),
// no Supabase query, no customer content rendered on delapresse.fr.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Rule } from "@/components/marketing/Rule";
import { CTABanner } from "@/components/marketing/CTABanner";
import { SiteFrame } from "@/components/marketing/SiteFrame";
import { buildBreadcrumbJsonLd, buildOrganizationJsonLd } from "@/lib/seo";
import { withUtm } from "@/lib/marketing-utm";
import { SHOWCASE_SITES, siteUrl, type SiteLang } from "@/lib/showcase-sites";
import { useLocale, intlLocale, pickLocalized } from "@/lib/locale";

type FilterValue = "all" | SiteLang;

const FILTERS: FilterValue[] = ["all", "fr", "en"];

export default function ShowcasePage() {
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const [filter, setFilter] = useState<FilterValue>("all");

  const sites = useMemo(
    () => (filter === "all" ? SHOWCASE_SITES : SHOWCASE_SITES.filter((s) => s.lang === filter)),
    [filter],
  );

  const totalArticles = SHOWCASE_SITES.reduce((n, s) => n + s.articles, 0);

  const jsonLd = useMemo(
    () => [
      buildOrganizationJsonLd(),
      buildBreadcrumbJsonLd([
        { name: t("marketing.breadcrumbs.home"), path: "/" },
        { name: t("marketing.header.nav.showcase"), path: "/vitrine" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: SHOWCASE_SITES.map((s, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          url: siteUrl(s),
          name: s.name,
        })),
      },
    ],
    [t],
  );

  return (
    <>
      <SeoHead
        title={t("marketing.pages.showcase.seo.title")}
        description={t("marketing.pages.showcase.seo.description")}
        path="/vitrine"
        locale={locale}
        jsonLd={jsonLd}
      />

      <Section padding="lg">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.showcase.hero.eyebrow")}</Eyebrow>
          <DisplayHeading level={1} className="mt-4">
            {t("marketing.pages.showcase.hero.title")}
          </DisplayHeading>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">
            {t("marketing.pages.showcase.hero.lead", {
              sites: SHOWCASE_SITES.length,
              articles: totalArticles.toLocaleString(intlLocale(locale)),
            })}
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2" role="group" aria-label={t("marketing.pages.showcase.filters.label")}>
          {FILTERS.map((f) => {
            const isActive = filter === f;
            return (
              <button
                key={f}
                type="button"
                aria-pressed={isActive}
                onClick={() => setFilter(f)}
                className={
                  "border px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors " +
                  (isActive
                    ? "border-foreground bg-foreground text-background"
                    : "border-rule text-foreground hover:border-foreground")
                }
              >
                {t(`marketing.pages.showcase.filters.${f}`)}
              </button>
            );
          })}
        </div>
      </Section>

      <Rule />

      <Section padding="md">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {sites.map((s, i) => (
            <article key={s.domain} className="lift-card flex flex-col border border-rule bg-background">
              <SiteFrame site={s} size="sm" eager={i < 3} className="border-0 shadow-none" />
              <div className="flex flex-1 flex-col border-t border-rule p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-display text-xl leading-tight">{s.name}</h2>
                    <p className="dateline mt-1 lowercase">{s.domain}</p>
                  </div>
                  <span className="dateline flex-none border border-rule px-1.5 py-0.5 text-[10px]">{s.lang.toUpperCase()}</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{pickLocalized(s.niche, locale)}</p>
                <dl className="mt-4 grid grid-cols-2 gap-3 border-y border-rule py-3 text-xs">
                  <div>
                    <dt className="text-muted-foreground">{t("marketing.pages.showcase.card.articles")}</dt>
                    <dd className="mt-1 text-sm font-medium tabular-nums">
                      {s.articles.toLocaleString(intlLocale(locale))}+
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t("marketing.pages.showcase.card.languages")}</dt>
                    <dd className="mt-1 text-sm font-medium">{t(`marketing.pages.showcase.filters.${s.lang}`)}</dd>
                  </div>
                </dl>
                <a
                  href={siteUrl(s)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent-press"
                >
                  {t("marketing.pages.showcase.card.viewSite")} <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              </div>
            </article>
          ))}
        </div>
      </Section>

      <CTABanner
        headline={t("marketing.pages.showcase.cta.headline")}
        subhead={t("marketing.pages.showcase.cta.subhead")}
        primary={{
          label: t("marketing.cta.startTrial"),
          to: withUtm("/dpadmin?intent=signup", { source: "showcase", campaign: "vitrine" }),
        }}
        secondary={{ label: t("marketing.cta.viewPricing"), to: withUtm("/tarifs", { source: "showcase", campaign: "vitrine" }) }}
        tone="ink"
      />
    </>
  );
}
